'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function TriangleCalculator() {
  const t = useTranslations("tools.triangleCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputMode, setInputMode] = useState<'SSS' | 'SAS' | 'ASA' | 'AAS' | 'SSA'>('SSS');
  const [inputs, setInputs] = useState({
    sideA: '',
    sideB: '',
    sideC: '',
    angleA: '',
    angleB: '',
    angleC: '',
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      lineHeight: 1.2,
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    summaryText: {
      color: colors.textSecondary,
      lineHeight: 1.6,
    },
    calculatorTool: {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      border: `1px solid ${colors.glassBorder}`,
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
    },
    toolContainer: {
      maxWidth: '100%',
      margin: '0 auto',
    },
    h2: {
      fontWeight: 600,
      color: colors.textPrimary,
    },
    label: {
      display: 'block',
      fontWeight: 600,
      color: colors.textPrimary,
      fontSize: '0.875rem',
    },
    input: {
      width: '100%',
      border: `2px solid ${colors.glassBorder}`,
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      outline: 'none',
      boxSizing: 'border-box' as const,
    },
    select: {
      width: '100%',
      border: `2px solid ${colors.glassBorder}`,
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      outline: 'none',
      cursor: 'pointer',
      background: 'white',
      boxSizing: 'border-box' as const,
    },
    calculateButton: {
      width: '100%',
      fontWeight: 700,
      color: '#fff',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(255, 90, 112, 0.3)',
    },
    resultsCard: {
      background: 'rgba(255, 255, 255, 0.9)',
      borderRadius: '0.75rem',
      border: `1px solid ${colors.glassBorder}`,
      boxShadow: '0 4px 15px rgba(255, 90, 112, 0.1)',
    },
    resultItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid rgba(255, 200, 180, 0.1)',
    },
    resultLabel: {
      fontWeight: 600,
      color: colors.textSecondary,
    },
    resultValue: {
      fontWeight: 700,
      color: colors.primary,
    },
    glassCard: {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
    },
    paragraph: {
      lineHeight: 1.7,
      color: colors.textSecondary,
    },
    list: {
      lineHeight: 1.8,
      color: colors.textSecondary,
      paddingLeft: '1.5rem',
    },
    listItem: {
      marginBottom: '0.75rem',
    },
    errorMessage: {
      background: 'rgba(255, 90, 112, 0.1)',
      border: `1px solid ${colors.primary}`,
      borderRadius: '0.5rem',
      color: colors.primary,
      fontWeight: 600,
    },
    badge: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      borderRadius: '1rem',
      fontSize: '0.875rem',
      fontWeight: 600,
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
      color: '#fff',
    },
    faqItem: {
      borderBottom: '1px solid rgba(255, 200, 180, 0.1)',
    },
    faqQuestion: {
      fontWeight: 600,
      color: colors.textPrimary,
    },
    faqAnswer: {
      color: colors.textSecondary,
      lineHeight: 1.7,
    },
    reference: {
      padding: '1rem',
      background: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '0.5rem',
      borderLeft: `4px solid ${colors.primary}`,
    },
    referenceTitle: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.25rem',
    },
    referenceUrl: {
      fontSize: '0.875rem',
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-all' as const,
    },
  };

  // Responsive styles
  const containerStyle = getResponsiveStyle(
    baseStyles.container,
    { padding: '1rem' },
    { padding: '2rem' },
    { padding: '3rem' }
  );

  const headerStyle = getResponsiveStyle(
    baseStyles.header,
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    baseStyles.h1,
    { fontSize: '2rem', marginBottom: '1rem' },
    { fontSize: '2.5rem', marginBottom: '1.25rem' },
    { fontSize: '3rem', marginBottom: '1.5rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    baseStyles.summaryText,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    baseStyles.calculatorTool,
    { padding: '1.5rem 1rem', marginBottom: '2rem' },
    { padding: '2rem 1.5rem', marginBottom: '2.5rem' },
    { padding: '3rem 2rem', marginBottom: '3rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    baseStyles.toolContainer,
    { maxWidth: '100%' },
    { maxWidth: '600px' },
    { maxWidth: '800px' }
  );

  const h2Style = getResponsiveStyle(
    baseStyles.h2,
    { fontSize: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const labelStyle = getResponsiveStyle(
    baseStyles.label,
    { marginBottom: '0.5rem' },
    { marginBottom: '0.625rem' },
    { marginBottom: '0.75rem' }
  );

  const inputStyle = getResponsiveStyle(
    baseStyles.input,
    { padding: '0.75rem 1rem', marginBottom: '1rem' },
    { padding: '0.875rem 1.25rem', marginBottom: '1.25rem' },
    { padding: '1rem 1.5rem', marginBottom: '1.5rem' }
  );

  const selectStyle = getResponsiveStyle(
    baseStyles.select,
    { padding: '0.75rem 1rem', marginBottom: '1rem' },
    { padding: '0.875rem 1.25rem', marginBottom: '1.25rem' },
    { padding: '1rem 1.5rem', marginBottom: '1.5rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    baseStyles.calculateButton,
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem' }
  );

  const resultsCardStyle = getResponsiveStyle(
    baseStyles.resultsCard,
    { padding: '1rem', marginTop: '1rem' },
    { padding: '1.5rem', marginTop: '1.5rem' },
    { padding: '2rem', marginTop: '2rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    baseStyles.glassCard,
    { padding: '1.5rem', marginBottom: '1.5rem' },
    { padding: '2rem', marginBottom: '2rem' },
    { padding: '2.5rem', marginBottom: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    baseStyles.paragraph,
    { fontSize: '1rem', marginBottom: '1rem' },
    { fontSize: '1.125rem', marginBottom: '1.25rem' },
    { fontSize: '1.25rem', marginBottom: '1.5rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    baseStyles.errorMessage,
    { padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem' },
    { padding: '1rem 1.25rem', marginBottom: '1.25rem', fontSize: '1rem' },
    { padding: '1rem 1.5rem', marginBottom: '1.5rem', fontSize: '1rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    baseStyles.resultItem,
    { padding: '0.75rem 0', fontSize: '0.875rem' },
    { padding: '1rem 0', fontSize: '1rem' },
    { padding: '1rem 0', fontSize: '1.125rem' }
  );

  const faqItemStyle = getResponsiveStyle(
    baseStyles.faqItem,
    { paddingBottom: '1rem', marginBottom: '1rem' },
    { paddingBottom: '1.25rem', marginBottom: '1.25rem' },
    { paddingBottom: '1.5rem', marginBottom: '1.5rem' }
  );

  const referenceStyle = getResponsiveStyle(
    baseStyles.reference,
    { marginBottom: '1rem' },
    { marginBottom: '1.25rem' },
    { marginBottom: '1.5rem' }
  );

  // Calculation functions
  const toRadians = (degrees: number) => degrees * (Math.PI / 180);
  const toDegrees = (radians: number) => radians * (180 / Math.PI);

  const validateTriangle = (a: number, b: number, c: number): boolean => {
    return a + b > c && a + c > b && b + c > a;
  };

  const calculateTriangle = () => {
    setError('');
    setResults(null);

    try {
      let a = 0, b = 0, c = 0;
      let angleA = 0, angleB = 0, angleC = 0;

      if (inputMode === 'SSS') {
        // Three sides
        a = parseFloat(inputs.sideA);
        b = parseFloat(inputs.sideB);
        c = parseFloat(inputs.sideC);

        if (!a || !b || !c || a <= 0 || b <= 0 || c <= 0) {
          setError(t('errorInvalidInput'));
          return;
        }

        if (!validateTriangle(a, b, c)) {
          setError(t('errorTriangleInequality'));
          return;
        }

        // Law of Cosines to find angles
        angleA = toDegrees(Math.acos((b * b + c * c - a * a) / (2 * b * c)));
        angleB = toDegrees(Math.acos((a * a + c * c - b * b) / (2 * a * c)));
        angleC = 180 - angleA - angleB;

      } else if (inputMode === 'SAS') {
        // Two sides and included angle
        a = parseFloat(inputs.sideA);
        b = parseFloat(inputs.sideB);
        angleC = parseFloat(inputs.angleC);

        if (!a || !b || !angleC || a <= 0 || b <= 0 || angleC <= 0 || angleC >= 180) {
          setError(t('errorInvalidInput'));
          return;
        }

        // Law of Cosines to find third side
        c = Math.sqrt(a * a + b * b - 2 * a * b * Math.cos(toRadians(angleC)));

        // Law of Cosines to find other angles
        angleA = toDegrees(Math.acos((b * b + c * c - a * a) / (2 * b * c)));
        angleB = 180 - angleA - angleC;

      } else if (inputMode === 'ASA') {
        // Two angles and included side
        angleA = parseFloat(inputs.angleA);
        angleB = parseFloat(inputs.angleB);
        c = parseFloat(inputs.sideC);

        if (!angleA || !angleB || !c || angleA <= 0 || angleB <= 0 || c <= 0 || angleA + angleB >= 180) {
          setError(t('errorInvalidInput'));
          return;
        }

        angleC = 180 - angleA - angleB;

        // Law of Sines
        a = c * Math.sin(toRadians(angleA)) / Math.sin(toRadians(angleC));
        b = c * Math.sin(toRadians(angleB)) / Math.sin(toRadians(angleC));

      } else if (inputMode === 'AAS') {
        // Two angles and non-included side
        angleA = parseFloat(inputs.angleA);
        angleB = parseFloat(inputs.angleB);
        a = parseFloat(inputs.sideA);

        if (!angleA || !angleB || !a || angleA <= 0 || angleB <= 0 || a <= 0 || angleA + angleB >= 180) {
          setError(t('errorInvalidInput'));
          return;
        }

        angleC = 180 - angleA - angleB;

        // Law of Sines
        b = a * Math.sin(toRadians(angleB)) / Math.sin(toRadians(angleA));
        c = a * Math.sin(toRadians(angleC)) / Math.sin(toRadians(angleA));

      } else if (inputMode === 'SSA') {
        // Two sides and non-included angle (ambiguous case)
        a = parseFloat(inputs.sideA);
        b = parseFloat(inputs.sideB);
        angleA = parseFloat(inputs.angleA);

        if (!a || !b || !angleA || a <= 0 || b <= 0 || angleA <= 0 || angleA >= 180) {
          setError(t('errorInvalidInput'));
          return;
        }

        // Law of Sines to find angle B
        const sinB = b * Math.sin(toRadians(angleA)) / a;

        if (sinB > 1) {
          setError(t('errorNoSolution'));
          return;
        }

        angleB = toDegrees(Math.asin(sinB));
        angleC = 180 - angleA - angleB;

        if (angleC <= 0) {
          setError(t('errorNoSolution'));
          return;
        }

        // Law of Sines to find side c
        c = a * Math.sin(toRadians(angleC)) / Math.sin(toRadians(angleA));
      }

      // Calculate area using Heron's formula
      const s = (a + b + c) / 2;
      const area = Math.sqrt(s * (s - a) * (s - b) * (s - c));

      // Calculate perimeter
      const perimeter = a + b + c;

      // Calculate heights
      const heightA = (2 * area) / a;
      const heightB = (2 * area) / b;
      const heightC = (2 * area) / c;

      // Determine triangle type
      let triangleType = '';
      const angles = [angleA, angleB, angleC].sort((x, y) => x - y);

      if (Math.abs(angles[2] - 90) < 0.01) {
        triangleType = t('typeRight');
      } else if (angles[2] > 90) {
        triangleType = t('typeObtuse');
      } else {
        triangleType = t('typeAcute');
      }

      if (Math.abs(a - b) < 0.01 && Math.abs(b - c) < 0.01) {
        triangleType += `, ${t('typeEquilateral')}`;
      } else if (Math.abs(a - b) < 0.01 || Math.abs(b - c) < 0.01 || Math.abs(a - c) < 0.01) {
        triangleType += `, ${t('typeIsosceles')}`;
      } else {
        triangleType += `, ${t('typeScalene')}`;
      }

      setResults({
        sideA: a.toFixed(4),
        sideB: b.toFixed(4),
        sideC: c.toFixed(4),
        angleA: angleA.toFixed(2),
        angleB: angleB.toFixed(2),
        angleC: angleC.toFixed(2),
        area: area.toFixed(4),
        perimeter: perimeter.toFixed(4),
        heightA: heightA.toFixed(4),
        heightB: heightB.toFixed(4),
        heightC: heightC.toFixed(4),
        triangleType,
      });

    } catch (err) {
      setError(t('errorCalculation'));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    calculateTriangle();
  };

  const renderInputFields = () => {
    switch (inputMode) {
      case 'SSS':
        return (
          <>
            <div>
              <label style={labelStyle}>{t('labelSideA')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.sideA}
                onChange={(e) => setInputs({ ...inputs, sideA: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderSideA')}
              />
            </div>
            <div>
              <label style={labelStyle}>{t('labelSideB')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.sideB}
                onChange={(e) => setInputs({ ...inputs, sideB: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderSideB')}
              />
            </div>
            <div>
              <label style={labelStyle}>{t('labelSideC')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.sideC}
                onChange={(e) => setInputs({ ...inputs, sideC: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderSideC')}
              />
            </div>
          </>
        );
      case 'SAS':
        return (
          <>
            <div>
              <label style={labelStyle}>{t('labelSideA')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.sideA}
                onChange={(e) => setInputs({ ...inputs, sideA: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderSideA')}
              />
            </div>
            <div>
              <label style={labelStyle}>{t('labelSideB')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.sideB}
                onChange={(e) => setInputs({ ...inputs, sideB: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderSideB')}
              />
            </div>
            <div>
              <label style={labelStyle}>{t('labelAngleC')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.angleC}
                onChange={(e) => setInputs({ ...inputs, angleC: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderAngleC')}
              />
            </div>
          </>
        );
      case 'ASA':
        return (
          <>
            <div>
              <label style={labelStyle}>{t('labelAngleA')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.angleA}
                onChange={(e) => setInputs({ ...inputs, angleA: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderAngleA')}
              />
            </div>
            <div>
              <label style={labelStyle}>{t('labelAngleB')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.angleB}
                onChange={(e) => setInputs({ ...inputs, angleB: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderAngleB')}
              />
            </div>
            <div>
              <label style={labelStyle}>{t('labelSideC')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.sideC}
                onChange={(e) => setInputs({ ...inputs, sideC: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderSideC')}
              />
            </div>
          </>
        );
      case 'AAS':
        return (
          <>
            <div>
              <label style={labelStyle}>{t('labelAngleA')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.angleA}
                onChange={(e) => setInputs({ ...inputs, angleA: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderAngleA')}
              />
            </div>
            <div>
              <label style={labelStyle}>{t('labelAngleB')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.angleB}
                onChange={(e) => setInputs({ ...inputs, angleB: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderAngleB')}
              />
            </div>
            <div>
              <label style={labelStyle}>{t('labelSideA')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.sideA}
                onChange={(e) => setInputs({ ...inputs, sideA: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderSideA')}
              />
            </div>
          </>
        );
      case 'SSA':
        return (
          <>
            <div>
              <label style={labelStyle}>{t('labelSideA')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.sideA}
                onChange={(e) => setInputs({ ...inputs, sideA: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderSideA')}
              />
            </div>
            <div>
              <label style={labelStyle}>{t('labelSideB')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.sideB}
                onChange={(e) => setInputs({ ...inputs, sideB: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderSideB')}
              />
            </div>
            <div>
              <label style={labelStyle}>{t('labelAngleA')}</label>
              <input
                type="number"
                step="0.01"
                value={inputs.angleA}
                onChange={(e) => setInputs({ ...inputs, angleA: e.target.value })}
                style={inputStyle}
                placeholder={t('placeholderAngleA')}
              />
            </div>
          </>
        );
    }
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <h2 style={h2Style}>{t('toolTitle')}</h2>

          <form onSubmit={handleSubmit}>
            <div>
              <label style={labelStyle}>{t('labelInputMode')}</label>
              <select
                value={inputMode}
                onChange={(e) => {
                  setInputMode(e.target.value as any);
                  setInputs({ sideA: '', sideB: '', sideC: '', angleA: '', angleB: '', angleC: '' });
                  setResults(null);
                  setError('');
                }}
                style={selectStyle}
              >
                <option value="SSS">{t('modeSSSLabel')}</option>
                <option value="SAS">{t('modeSASLabel')}</option>
                <option value="ASA">{t('modeASALabel')}</option>
                <option value="AAS">{t('modeAASLabel')}</option>
                <option value="SSA">{t('modeSSALabel')}</option>
              </select>
            </div>

            {renderInputFields()}

            <button
              type="submit"
              style={calculateButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 90, 112, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 90, 112, 0.3)';
              }}
            >
              {t('calculateButton')}
            </button>
          </form>

          {error && (
            <div style={errorMessageStyle}>
              {error}
            </div>
          )}

          {results && (
            <div style={resultsCardStyle}>
              <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginBottom: '1rem' }}>
                {t('resultsTitle')}
              </h3>

              <div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('resultSideA')}</span>
                  <span style={baseStyles.resultValue}>{results.sideA}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('resultSideB')}</span>
                  <span style={baseStyles.resultValue}>{results.sideB}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('resultSideC')}</span>
                  <span style={baseStyles.resultValue}>{results.sideC}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('resultAngleA')}</span>
                  <span style={baseStyles.resultValue}>{results.angleA}°</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('resultAngleB')}</span>
                  <span style={baseStyles.resultValue}>{results.angleB}°</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('resultAngleC')}</span>
                  <span style={baseStyles.resultValue}>{results.angleC}°</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('resultArea')}</span>
                  <span style={baseStyles.resultValue}>{results.area}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('resultPerimeter')}</span>
                  <span style={baseStyles.resultValue}>{results.perimeter}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('resultHeightA')}</span>
                  <span style={baseStyles.resultValue}>{results.heightA}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('resultHeightB')}</span>
                  <span style={baseStyles.resultValue}>{results.heightB}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={baseStyles.resultLabel}>{t('resultHeightC')}</span>
                  <span style={baseStyles.resultValue}>{results.heightC}</span>
                </div>
                <div style={{ ...resultItemStyle, borderBottom: 'none' }}>
                  <span style={baseStyles.resultLabel}>{t('resultTriangleType')}</span>
                  <span style={baseStyles.resultValue}>{results.triangleType}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t('howToUseStep1')}</li>
          <li style={baseStyles.listItem}>{t('howToUseStep2')}</li>
          <li style={baseStyles.listItem}>{t('howToUseStep3')}</li>
          <li style={baseStyles.listItem}>{t('howToUseStep4')}</li>
          <li style={baseStyles.listItem}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      {/* Key Features */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('featuresTitle')}</h2>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>
            <strong>{t('feature1Title')}</strong> {t('feature1Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('feature2Title')}</strong> {t('feature2Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('feature3Title')}</strong> {t('feature3Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('feature4Title')}</strong> {t('feature4Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('feature5Title')}</strong> {t('feature5Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('feature6Title')}</strong> {t('feature6Description')}
          </li>
        </ul>
      </section>

      {/* Mathematical Basis */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('mathTitle')}</h2>
        <p style={paragraphStyle}>{t('mathIntro')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '1.5rem' }}>
          {t('mathTheorem1Title')}
        </h3>
        <p style={paragraphStyle}>{t('mathTheorem1Description')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '1.5rem' }}>
          {t('mathTheorem2Title')}
        </h3>
        <p style={paragraphStyle}>{t('mathTheorem2Description')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '1.5rem' }}>
          {t('mathTheorem3Title')}
        </h3>
        <p style={paragraphStyle}>{t('mathTheorem3Description')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '1.5rem' }}>
          {t('mathTheorem4Title')}
        </h3>
        <p style={paragraphStyle}>{t('mathTheorem4Description')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '1.5rem' }}>
          {t('mathTheorem5Title')}
        </h3>
        <p style={paragraphStyle}>{t('mathTheorem5Description')}</p>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>
            <strong>{t('practice1Title')}</strong> {t('practice1Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('practice2Title')}</strong> {t('practice2Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('practice3Title')}</strong> {t('practice3Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('practice4Title')}</strong> {t('practice4Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('practice5Title')}</strong> {t('practice5Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('practice6Title')}</strong> {t('practice6Description')}
          </li>
        </ul>
      </section>

      {/* Common Use Cases */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('useCasesTitle')}</h2>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>
            <strong>{t('useCase1Title')}</strong> {t('useCase1Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('useCase2Title')}</strong> {t('useCase2Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('useCase3Title')}</strong> {t('useCase3Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('useCase4Title')}</strong> {t('useCase4Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('useCase5Title')}</strong> {t('useCase5Description')}
          </li>
          <li style={baseStyles.listItem}>
            <strong>{t('useCase6Title')}</strong> {t('useCase6Description')}
          </li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={baseStyles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={baseStyles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={baseStyles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={baseStyles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={baseStyles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={baseStyles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={baseStyles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={baseStyles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={baseStyles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={baseStyles.faqAnswer}>{t('faq5Answer')}</p>
        </div>

        <div style={{ ...faqItemStyle, borderBottom: 'none', paddingBottom: 0, marginBottom: 0 }}>
          <h3 style={baseStyles.faqQuestion}>{t('faq6Question')}</h3>
          <p style={baseStyles.faqAnswer}>{t('faq6Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>

        <div style={referenceStyle}>
          <div style={baseStyles.referenceTitle}>{t('reference1Title')}</div>
          <a
            href="https://www.calculator.io/triangle-calculator/"
            target="_blank"
            rel="noopener noreferrer"
            style={baseStyles.referenceUrl}
          >
            {t('reference1Url')}
          </a>
        </div>

        <div style={referenceStyle}>
          <div style={baseStyles.referenceTitle}>{t('reference2Title')}</div>
          <a
            href="https://www.omnicalculator.com/math/triangle"
            target="_blank"
            rel="noopener noreferrer"
            style={baseStyles.referenceUrl}
          >
            {t('reference2Url')}
          </a>
        </div>

        <div style={referenceStyle}>
          <div style={baseStyles.referenceTitle}>{t('reference3Title')}</div>
          <a
            href="https://www.mathsisfun.com/geometry/triangles.html"
            target="_blank"
            rel="noopener noreferrer"
            style={baseStyles.referenceUrl}
          >
            {t('reference3Url')}
          </a>
        </div>

        <div style={{ ...referenceStyle, marginBottom: 0 }}>
          <div style={baseStyles.referenceTitle}>{t('reference4Title')}</div>
          <a
            href="https://en.wikipedia.org/wiki/Triangle"
            target="_blank"
            rel="noopener noreferrer"
            style={baseStyles.referenceUrl}
          >
            {t('reference4Url')}
          </a>
        </div>
      </section>
    </div>
  );
}